import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { supportedLanguages } from '../i18n';
import { trackEvent, EVENTS } from '../utils/analytics';
import { setLanguage } from '../utils/api';
import { useUser } from '../context/UserContext';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';

const LanguageFlag = ({ flagCode, className = '' }) => (
  <img
    src={`/assets/flags/${flagCode}.svg`}
    alt=""
    width={20}
    height={15}
    className={`w-[20px] h-[15px] object-cover rounded-[2px] flex-shrink-0 ring-1 ring-black/10 dark:ring-white/35 ${className}`}
    loading="lazy"
  />
);

/**
 * LanguageSelector component for switching between languages
 * Displays a dropdown with available languages and persists selection
 * Language fetching is handled by UserContext via react-query
 */
const LanguageSelector = ({ variant = 'default', isCollapsed = false }) => {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useUser();
  const { fontSize } = useGlobalFontSize();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get current language object
  const currentLanguage = supportedLanguages.find(
    (lang) => lang.code === i18n.language
  ) || supportedLanguages[0];

  // Handle language change
  const handleLanguageChange = async (langCode) => {
    const previousLanguage = i18n.language;
    
    // Update UI immediately
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    
    // Track analytics
    trackEvent(EVENTS.LANGUAGE_CHANGED, { 
      from_language: previousLanguage, 
      to_language: langCode 
    });

    // Save to backend if authenticated
    if (isAuthenticated) {
      try {
        await setLanguage(langCode);
      } catch (error) {
        console.error('Failed to save language preference:', error);
        // Language is already changed in UI, so no rollback needed
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (variant === 'sidebar') {
    return (
      <div className="relative w-full" ref={dropdownRef}>
        {/* min-h-11 = 44px touch target, applied below lg only — this trigger is
            also rendered in the desktop collapsible left-nav (DashboardLayout
            with isCollapsed), so the touch-target height must not leak into
            desktop. Below lg it's the mobile nav drawer (DashboardLayout's
            SidebarFooter), the closest live equivalent to portrait-signage's
            compact portrait header trigger (ported from 18a161b/d1e391d).
            Dev tokens/colors are unchanged; only the touch-target height is added. */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full min-h-11 lg:min-h-0 flex cursor-pointer items-center gap-[8px] py-[8px] rounded-[4px] hover:bg-[#E8EDEA] dark:hover:bg-[#282a29]/50 transition-colors ${
            isCollapsed ? 'justify-center' : ''
          }`}
          aria-label="Select language"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <LanguageFlag flagCode={currentLanguage.flagCode} />
          {!isCollapsed && (
            <>
              <span
                className="font-['Inter'] text-[#444746] dark:text-[#f0f5f2] flex-1 text-left"
                style={{ fontSize }}
              >
                {currentLanguage.name}
              </span>
              <ChevronDown
                size={16}
                className={`text-[#444746] dark:text-[#f0f5f2] flex-shrink-0 transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </>
          )}
        </button>

        {isOpen && (
          <div
            className={`absolute left-0 bottom-full mb-2 bg-[#f9f9f9] dark:bg-[#282a29] border border-[#C3C4C3] dark:border-[#5d625f] rounded-[8px] shadow-xl z-[100] overflow-hidden ${
              isCollapsed ? 'w-auto' : 'w-full'
            }`}
            role="listbox"
            aria-label="Language options"
          >
            {supportedLanguages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageChange(language.code)}
                className={`w-full flex cursor-pointer items-center transition-colors ${
                  isCollapsed
                    ? 'justify-center px-[12px] py-[10px]'
                    : 'gap-[12px] px-[16px] py-[12px] text-left'
                } ${
                  currentLanguage.code === language.code
                    ? 'bg-bloom-green/20 text-bloom-green'
                    : 'text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#E8EDEA] dark:hover:bg-[#1d1d1d]'
                }`}
                role="option"
                aria-selected={currentLanguage.code === language.code}
                aria-label={language.name}
              >
                <LanguageFlag flagCode={language.flagCode} />
                {!isCollapsed && (
                  <span className="font-['Inter']" style={{ fontSize }}>
                    {language.name}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // TODO(portrait): product decision — this default/header variant is not
  // currently rendered in any top bar (dev's mobile header only shows a
  // hamburger; language selection moved into the drawer's `sidebar` variant
  // above). portrait-signage 18a161b compacted this trigger to flag-only
  // below `sm:`; left byte-identical here since there's no live portrait
  // header consumer to adapt against. Revisit if a header chip is reintroduced.
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-[8px] px-[12px] py-[8px] rounded-[8px] bg-[#f9f9f9] dark:bg-[#282a29] hover:bg-[#E8EDEA] dark:hover:bg-[#282a29]/80 transition-colors"
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <LanguageFlag flagCode={currentLanguage.flagCode} />
        <span 
          className="font-['Inter'] text-[#282A29] dark:text-[#f0f5f2] hidden sm:inline" 
          style={{ fontSize: fontSize }}
        >
          {currentLanguage.name}
        </span>
        <ChevronDown
          size={16}
          className={`text-[#282A29] dark:text-[#f0f5f2] transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-[160px] bg-[#f9f9f9] dark:bg-[#282a29] border border-[#C3C4C3] dark:border-[#5d625f] rounded-[8px] shadow-xl z-[100] overflow-hidden"
          role="listbox"
          aria-label="Language options"
        >
          {supportedLanguages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={`w-full flex items-center gap-[12px] px-[16px] py-[12px] text-left transition-colors ${
                currentLanguage.code === language.code
                  ? 'bg-bloom-green/20 text-bloom-green'
                  : 'text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#E8EDEA] dark:hover:bg-[#1d1d1d]'
              }`}
              role="option"
              aria-selected={currentLanguage.code === language.code}
            >
              <LanguageFlag flagCode={language.flagCode} />
              <span 
                className="font-['Inter']"
                style={{ fontSize: fontSize }}
              >
                {language.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;

